/**
 * Client-side mirror of server types (subset). 서버 src/services/messages/types.ts 와 sync.
 * 데이터만, runtime logic 없음.
 */

export type ConversationKind = 'direct' | 'proposal' | 'invitation' | 'system';

export type ConversationStatus =
 | 'open' | 'archived' | 'acknowledged'
 | 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'countered' | 'expired'
 | 'contracted' | 'paid' | 'disputed' | 'settled';

export type ParticipantRole = 'sender' | 'recipient' | 'observer' | 'cc';

export type MessageType =
 | 'text' | 'system_notice'
 | 'proposal_offer' | 'proposal_counter' | 'proposal_accept' | 'proposal_reject' | 'proposal_cancel'
 | 'invitation_offer' | 'invitation_counter' | 'invitation_accept' | 'invitation_reject';

export interface InboxItem {
 id: string;
 kind: ConversationKind;
 subject: string | null;
 status: ConversationStatus;
 context_ref: Record<string, unknown>;
 version: number;
 created_at: string;
 last_message_at: string | null;
 my_role: ParticipantRole;
 last_read_at: string | null;
 is_archived: boolean;
 unread: boolean;
}

export interface Conversation {
 id: string;
 kind: ConversationKind;
 subject: string | null;
 status: ConversationStatus;
 context_ref: Record<string, unknown>;
 version: number;
 created_at: string;
 updated_at: string;
 last_message_at: string | null;
}

export interface Message {
 id: string;
 conversation_id: string;
 sender_peer_hash: string | null;
 body: string | null;
 payload: Record<string, unknown>;
 message_type: MessageType;
 attachments: Array<{ file_hash: string; role: string }>;
 created_at: string;
}

export interface Participant {
 participant_id: string;
 conversation_id: string;
 peer_hash: string | null;
 family_hash: string | null;
 acting_peer_hash: string | null;
 role: ParticipantRole;
 joined_at: string;
 last_read_at: string | null;
 is_archived: boolean;
 revoked_at: string | null;
 // 서버 enrichment 필드 (optional — backward compat)
 peer_name?: string | null;
 peer_email?: string | null;
 peer_profile_image?: string | null;
 family_name?: string | null;
 family_image_url?: string | null;
}

export const KIND_LABEL: Record<ConversationKind, string> = {
 direct: '메시지',
 proposal: '마켓 제안',
 invitation: '가입 초대',
 system: '시스템',
};

export const STATUS_LABEL: Partial<Record<ConversationStatus, string>> = {
 open: '진행 중',
 archived: '보관됨',
 acknowledged: '확인됨',
 pending: '대기 중',
 accepted: '수락됨',
 rejected: '거절됨',
 cancelled: '취소됨',
 countered: '역제안',
 expired: '만료됨',
 contracted: '계약됨',
 paid: '결제 완료',
 disputed: '분쟁',
 settled: '정산 완료',
};

export const KIND_COLOR: Record<ConversationKind, string> = {
 direct: 'bg-gray-100 text-gray-700',
 proposal: 'bg-blue-100 text-blue-700',
 invitation: 'bg-purple-100 text-purple-700',
 system: 'bg-yellow-100 text-yellow-700',
};

/** 메시지 타입의 한글 라벨 — MessageItem 의 배지 표시용. */
export const MESSAGE_TYPE_LABEL: Record<MessageType, string> = {
 text: '메시지',
 system_notice: '상태 변경',
 proposal_offer: '제안',
 proposal_counter: '역제안',
 proposal_accept: '수락',
 proposal_reject: '거절',
 proposal_cancel: '취소',
 invitation_offer: '가입 제안',
 invitation_counter: '역제안',
 invitation_accept: '수락',
 invitation_reject: '거절',
};

/** 타임라인 dot 색상 — message_type 별. */
export const MESSAGE_TYPE_DOT: Record<MessageType, string> = {
 text: 'bg-gray-400',
 system_notice: 'bg-yellow-400',
 proposal_offer: 'bg-blue-500',
 proposal_counter: 'bg-orange-500',
 proposal_accept: 'bg-green-500',
 proposal_reject: 'bg-red-500',
 proposal_cancel: 'bg-gray-400',
 invitation_offer: 'bg-purple-500',
 invitation_counter: 'bg-orange-500',
 invitation_accept: 'bg-green-500',
 invitation_reject: 'bg-red-500',
};

export interface TransitionInput {
 new_status: ConversationStatus;
 trigger_message_id?: string;
 context_ref_patch?: Record<string, unknown>;
 append_message?: {
  message_type: MessageType;
  body?: string | null;
  payload?: Record<string, unknown>;
  attachments?: Array<{ file_hash: string; role: string }>;
 };
}

export interface PostMessageInput {
 message_type: MessageType;
 body?: string | null;
 payload?: Record<string, unknown>;
 attachments?: Array<{ file_hash: string; role: string }>;
}
